import { useEffect, useState } from 'react';
import { debug } from '../utils/debug';
import { isMaxWebApp } from '../utils/platform';
import type { TelegramWebApp } from '../types';

/**
 * Fallback timeout before we give up waiting for Telegram SDK and mark the hook
 * as ready anyway. Prevents the app from freezing on a splash screen when it
 * runs outside Telegram (Max miniapp, standalone web).
 */
const SDK_WAIT_TIMEOUT_MS = 1500;

interface UseTelegramReturn {
  tg: TelegramWebApp | null;
  user: TelegramWebApp['initDataUnsafe']['user'] | null;
  isReady: boolean;
  colorScheme: 'light' | 'dark';
  themeParams: TelegramWebApp['themeParams'];
}

export const useTelegram = (): UseTelegramReturn => {
  const [isReady, setIsReady] = useState(false);
  const [tg, setTg] = useState<TelegramWebApp | null>(null);

  useEffect(() => {
    const inMax = isMaxWebApp();

    // Outside Telegram (e.g. Max miniapp or standalone web) don't even wait for
    // the SDK — immediately unblock the app.
    if (inMax) {
      setIsReady(true);
      return;
    }

    // Debug logging for Telegram SDK availability
    debug.log('🔍 useTelegram - SDK check:', {
      windowTelegram: !!window.Telegram,
      webApp: !!window.Telegram?.WebApp,
      initData: window.Telegram?.WebApp?.initData,
      initDataUnsafe: window.Telegram?.WebApp?.initDataUnsafe,
      user: window.Telegram?.WebApp?.initDataUnsafe?.user,
    });

    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const initTelegram = () => {
      if (cancelled) return;
      if (window.Telegram?.WebApp) {
        const telegram = window.Telegram.WebApp;
        setTg(telegram);

        debug.log('✅ useTelegram - Initializing Telegram WebApp:', {
          version: telegram.version,
          platform: telegram.platform,
          colorScheme: telegram.colorScheme,
          initData: telegram.initData,
          user: telegram.initDataUnsafe?.user,
        });

        try {
          telegram.ready();
          telegram.expand();
          telegram.MainButton.hide();
        } catch (err) {
          debug.warn('useTelegram - SDK call failed:', (err as Error)?.message);
        }

        setIsReady(true);
        if (fallbackTimer) clearTimeout(fallbackTimer);
      } else {
        debug.warn('⚠️ useTelegram - Telegram WebApp is not available, retrying...');
        retryTimer = setTimeout(initTelegram, 100);
      }
    };

    // Hard stop on the retry loop: after SDK_WAIT_TIMEOUT_MS, proceed without SDK.
    fallbackTimer = setTimeout(() => {
      if (cancelled) return;
      if (!window.Telegram?.WebApp) {
        setIsReady(true);
      }
    }, SDK_WAIT_TIMEOUT_MS);

    initTelegram();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
  }, []);

  return {
    tg,
    user: tg?.initDataUnsafe?.user || null,
    isReady,
    colorScheme: tg?.colorScheme || 'light',
    themeParams: tg?.themeParams || {},
  };
};

// Hook for haptic feedback
export const useHapticFeedback = () => {
  const { tg } = useTelegram();

  const impact = (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'medium') => {
    if (tg?.HapticFeedback) {
      tg.HapticFeedback.impactOccurred(style);
    }
  };

  const notification = (type: 'error' | 'success' | 'warning') => {
    if (tg?.HapticFeedback) {
      tg.HapticFeedback.notificationOccurred(type);
    }
  };

  const selection = () => {
    if (tg?.HapticFeedback) {
      tg.HapticFeedback.selectionChanged();
    }
  };

  return { impact, notification, selection };
};

// Hook for main button control
export const useMainButton = () => {
  const { tg } = useTelegram();

  const show = (text: string, onClick: () => void) => {
    if (tg?.MainButton) {
      tg.MainButton.setText(text);
      tg.MainButton.onClick(onClick);
      tg.MainButton.show();
    }
  };

  const hide = () => {
    if (tg?.MainButton) {
      tg.MainButton.hide();
    }
  };

  const showProgress = () => {
    if (tg?.MainButton) {
      tg.MainButton.showProgress();
    }
  };

  const hideProgress = () => {
    if (tg?.MainButton) {
      tg.MainButton.hideProgress();
    }
  };

  return { show, hide, showProgress, hideProgress };
}; 